<template>
  <div
    v-if="mobile && open"
    class="dp-nav-backdrop"
    aria-hidden="true"
    @click="requestClose"
  ></div>
  <aside
    id="data-portal-navigation"
    ref="navElement"
    class="dp-sidenav"
    :class="{ 'is-open': open, 'is-mobile': mobile }"
    :aria-hidden="!open"
    :inert="!open"
    @keydown="handleKeydown"
  >
    <div class="dp-nav-heading">
      <span>{{ t('dp.shell.sideNav.heading') }}</span>
      <button
        v-if="mobile"
        ref="closeButton"
        class="dp-nav-close"
        type="button"
        :aria-label="t('dp.shell.sideNav.close')"
        @click="requestClose"
      >
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <nav :aria-label="t('dp.shell.sideNav.navLabel')">
      <RouterLink
        class="dp-nav-link"
        :class="{ 'is-active': route.name === 'data-portal-home' }"
        :to="{ name: 'data-portal-home' }"
        :aria-current="route.name === 'data-portal-home' ? 'page' : undefined"
      >
        <i class="pi pi-home" aria-hidden="true"></i>
        <span>{{ t('dp.shell.sideNav.home') }}</span>
      </RouterLink>

      <section class="dp-nav-group" aria-labelledby="dp-views-label">
        <div class="dp-nav-row">
          <RouterLink
            class="dp-nav-link has-toggle"
            :class="{ 'is-active': route.name === 'data-portal-views' }"
            :to="{ name: 'data-portal-views' }"
            :aria-current="
              route.name === 'data-portal-views' ? 'page' : undefined
            "
          >
            <i class="pi pi-table" aria-hidden="true"></i>
            <span id="dp-views-label">{{ t('dp.shell.sideNav.views') }}</span>
          </RouterLink>
          <button
            class="dp-nav-toggle"
            type="button"
            :aria-expanded="viewsExpanded"
            aria-controls="dp-nav-views"
            :aria-label="
              viewsExpanded
                ? t('dp.shell.sideNav.collapseViews')
                : t('dp.shell.sideNav.expandViews')
            "
            @click="viewsExpanded = !viewsExpanded"
          >
            <i
              class="pi pi-chevron-down"
              :class="{ 'is-collapsed': !viewsExpanded }"
              aria-hidden="true"
            ></i>
          </button>
        </div>
        <div v-show="viewsExpanded" id="dp-nav-views">
          <div
            v-if="viewsStore.loading && !viewsStore.loaded"
            class="dp-nav-note"
          >
            {{ t('dp.shell.sideNav.viewsLoading') }}
          </div>
          <div v-else-if="viewsStore.error" class="dp-nav-note is-error">
            {{ t('dp.shell.sideNav.catalogUnavailable') }}
            <button
              v-if="auth.status === 'anonymous'"
              type="button"
              @click="login"
            >
              {{ t('dp.shell.sideNav.loginAgain') }}
            </button>
            <button v-else type="button" @click="reloadViews">
              {{ t('dp.shell.sideNav.retry') }}
            </button>
          </div>
          <RouterLink
            v-for="view in viewsStore.views"
            :key="view.key"
            class="dp-nav-link dp-view-link"
            :class="{ 'is-active': activeViewKey === view.key }"
            :to="{
              name: 'data-portal-view-details',
              params: { viewKey: view.key },
            }"
            :aria-current="activeViewKey === view.key ? 'page' : undefined"
          >
            <i class="pi pi-angle-right" aria-hidden="true"></i>
            <span>{{ viewTitle(view) }}</span>
          </RouterLink>
        </div>
      </section>

      <section
        v-if="isAdmin"
        class="dp-nav-group"
        aria-labelledby="dp-admin-label"
      >
        <h2 id="dp-admin-label">{{ t('dp.shell.sideNav.administration') }}</h2>
        <RouterLink
          class="dp-nav-link"
          :class="{ 'is-active': route.name === 'data-portal-audit' }"
          :to="{ name: 'data-portal-audit' }"
          :aria-current="
            route.name === 'data-portal-audit' ? 'page' : undefined
          "
        >
          <i class="pi pi-list" aria-hidden="true"></i>
          <span>{{ t('dp.shell.sideNav.audit') }}</span>
        </RouterLink>
        <RouterLink
          class="dp-nav-link"
          :class="{ 'is-active': isAdminViewsRoute }"
          :to="{ name: 'data-portal-admin-views' }"
          :aria-current="
            route.name === 'data-portal-admin-views' ? 'page' : undefined
          "
        >
          <i class="pi pi-table" aria-hidden="true"></i>
          <span>{{ t('dp.shell.sideNav.manageViews') }}</span>
        </RouterLink>
        <RouterLink
          class="dp-nav-link"
          :class="{ 'is-active': route.name === 'data-portal-admin-allowlist' }"
          :to="{ name: 'data-portal-admin-allowlist' }"
          :aria-current="
            route.name === 'data-portal-admin-allowlist' ? 'page' : undefined
          "
        >
          <i class="pi pi-database" aria-hidden="true"></i>
          <span>{{ t('dp.shell.sideNav.allowlist') }}</span>
        </RouterLink>
        <RouterLink
          class="dp-nav-link"
          :class="{ 'is-active': route.name === 'data-portal-admin-users' }"
          :to="{ name: 'data-portal-admin-users' }"
          :aria-current="
            route.name === 'data-portal-admin-users' ? 'page' : undefined
          "
        >
          <i class="pi pi-users" aria-hidden="true"></i>
          <span>{{ t('dp.shell.sideNav.users') }}</span>
        </RouterLink>
      </section>
    </nav>

    <div class="dp-nav-user">
      <span class="dp-user-avatar" aria-hidden="true">{{ initials }}</span>
      <span class="dp-user-copy">
        <strong>{{ displayName }}</strong>
        <small>{{ roleLabel }}</small>
      </span>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDataPortalAuthStore } from '../auth/data-portal-auth.store';
import type { DataPortalUser } from '../auth/data-portal-auth.types';
import { viewTitle } from '../api/data-portal-views.api';
import { useDataPortalViewsStore } from '../stores/data-portal-views.store';
import { useDataPortalI18n } from '../i18n';

const props = defineProps<{
  open: boolean;
  mobile: boolean;
  user: DataPortalUser;
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useDataPortalI18n();
const route = useRoute();
const auth = useDataPortalAuthStore();
const viewsStore = useDataPortalViewsStore();
const navElement = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const viewsExpanded = ref(true);
const isAdmin = computed(() => props.user.roles.includes('data-portal-admin'));
const activeViewKey = computed(() =>
  route.name === 'data-portal-view-details' &&
  typeof route.params.viewKey === 'string'
    ? route.params.viewKey
    : '',
);
const roleLabel = computed(() =>
  isAdmin.value
    ? t('dp.shell.sideNav.roleAdmin')
    : t('dp.shell.sideNav.roleUser'),
);
const displayName = computed(() => {
  if (props.user.name) return props.user.name;
  const identifier = props.user.username || props.user.email;
  if (!identifier) return t('dp.shell.sideNav.signedInAccount');
  if (!identifier.includes('@')) return identifier;
  const [localPart] = identifier.split('@');
  return `${localPart.slice(0, 2)}...`;
});
const initials = computed(() => {
  const parts = displayName.value.replace('...', '').trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
});

const isAdminViewsRoute = computed(
  () =>
    route.name === 'data-portal-admin-views' ||
    route.name === 'data-portal-admin-view',
);

function reloadViews(): void {
  void viewsStore.load(true);
}

function login(): void {
  auth.login(route.fullPath);
}

function getFocusableElements(): HTMLElement[] {
  if (!navElement.value) return [];
  return Array.from(
    navElement.value.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"]):not([disabled])',
    ),
  );
}

function requestClose(): void {
  emit('close');
}

function handleKeydown(event: KeyboardEvent): void {
  if (!props.mobile || !props.open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    requestClose();
    return;
  }
  if (event.key !== 'Tab') return;

  const focusableElements = getFocusableElements();
  if (!focusableElements.length) {
    event.preventDefault();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (event.shiftKey && activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  } else if (!navElement.value?.contains(activeElement)) {
    event.preventDefault();
    firstElement.focus();
  }
}

// A view opened elsewhere (cards, deep link) is shown and selected in the
// navigation: expand the group and bring the active entry into view.
async function revealActiveView(): Promise<void> {
  if (!activeViewKey.value) return;
  viewsExpanded.value = true;
  await nextTick();
  navElement.value
    ?.querySelector<HTMLElement>('.dp-view-link.is-active')
    ?.scrollIntoView({ block: 'nearest' });
}

watch(activeViewKey, revealActiveView);
watch(() => viewsStore.loaded, revealActiveView);

watch(
  () => props.open,
  async (open) => {
    if (!open || !props.mobile) return;
    await nextTick();
    (closeButton.value || getFocusableElements()[0])?.focus();
  },
);

onMounted(() => viewsStore.load());
</script>

<style scoped>
.dp-sidenav {
  width: 18.5rem;
  box-sizing: border-box;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem 1.25rem;
  color: var(--dp-color-surface-muted);
  background:
    radial-gradient(
      circle at 15% 90%,
      color-mix(in srgb, var(--dp-color-teal) 22%, transparent),
      transparent 18rem
    ),
    linear-gradient(
      165deg,
      var(--dp-color-navy) 0%,
      var(--dp-color-navy-deep) 100%
    );
  box-shadow: var(--dp-shadow-md);
  transition:
    width 220ms ease,
    padding 220ms ease,
    transform 220ms ease;
  overflow: hidden;
}
.dp-sidenav:not(.is-open):not(.is-mobile) {
  width: 0;
  padding-right: 0;
  padding-left: 0;
}
.dp-nav-heading {
  min-width: 16.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2.75rem;
  padding: 0 0.65rem;
  color: color-mix(in srgb, var(--dp-color-surface) 58%, var(--dp-color-teal));
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.dp-nav-close {
  width: 2.75rem;
  height: 2.75rem;
  border: 0;
  border-radius: var(--dp-radius-sm);
  color: var(--dp-color-surface);
  background: color-mix(in srgb, var(--dp-color-surface) 9%, transparent);
  cursor: pointer;
}
.dp-nav-group {
  min-width: 16.5rem;
  margin-top: 1.75rem;
}
.dp-nav-group h2 {
  margin: 0 0 0.45rem;
  padding: 0 0.75rem;
  color: color-mix(in srgb, var(--dp-color-surface) 58%, var(--dp-color-teal));
  font: 800 0.66rem/1.4 var(--dp-font-body);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.dp-nav-link {
  position: relative;
  min-width: 16.5rem;
  width: 100%;
  box-sizing: border-box;
  min-height: 3rem;
  display: grid;
  grid-template-columns: 1.35rem 1fr auto;
  align-items: center;
  gap: 0.7rem;
  margin: 0.18rem 0;
  padding: 0.65rem 0.75rem;
  border: 0;
  border-radius: var(--dp-radius-sm);
  color: color-mix(in srgb, var(--dp-color-surface) 86%, var(--dp-color-teal));
  background: transparent;
  font: inherit;
  text-align: left;
  text-decoration: none;
  transition:
    color 160ms ease,
    background 160ms ease;
}
.dp-nav-link::before {
  position: absolute;
  inset: 0 auto 0 -0.85rem;
  width: 0.25rem;
  border-radius: 0 0.2rem 0.2rem 0;
  background: var(--dp-color-orange);
  content: '';
  opacity: 0;
}
a.dp-nav-link:hover {
  color: var(--dp-color-surface);
  background: color-mix(in srgb, var(--dp-color-surface) 9%, transparent);
}
.dp-nav-link.is-active {
  color: var(--dp-color-surface);
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--dp-color-orange) 24%, transparent),
    color-mix(in srgb, var(--dp-color-teal) 12%, transparent)
  );
  font-weight: 700;
}
.dp-nav-link.is-active::before {
  opacity: 1;
}
.dp-nav-link i {
  color: color-mix(in srgb, var(--dp-color-teal) 72%, var(--dp-color-surface));
  font-size: 1.05rem;
  text-align: center;
}
.dp-nav-link.is-active i {
  color: var(--dp-color-orange);
}
.dp-nav-row {
  position: relative;
}
.dp-nav-link.has-toggle {
  padding-right: 2.75rem;
}
.dp-nav-toggle {
  position: absolute;
  top: 50%;
  right: 0.35rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: var(--dp-radius-sm);
  color: color-mix(in srgb, var(--dp-color-teal) 72%, var(--dp-color-surface));
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);
}
.dp-nav-toggle:hover {
  color: var(--dp-color-surface);
  background: color-mix(in srgb, var(--dp-color-surface) 12%, transparent);
}
.dp-nav-toggle:focus-visible {
  outline: 3px solid var(--dp-color-surface);
  outline-offset: 2px;
}
.dp-nav-toggle i {
  font-size: 0.8rem;
  transition: transform 160ms ease;
}
.dp-nav-toggle i.is-collapsed {
  transform: rotate(-90deg);
}
.dp-view-link {
  min-height: 2.55rem;
  padding-left: 1.15rem;
  font-size: 0.82rem;
}
.dp-view-link i {
  font-size: 0.8rem;
}
.dp-nav-note {
  display: grid;
  gap: 0.35rem;
  padding: 0.55rem 0.75rem;
  color: color-mix(in srgb, var(--dp-color-surface) 60%, var(--dp-color-teal));
  font-size: 0.7rem;
}
.dp-nav-note.is-error {
  color: color-mix(
    in srgb,
    var(--dp-color-surface) 72%,
    var(--dp-color-orange)
  );
}
.dp-nav-note button {
  width: max-content;
  padding: 0;
  border: 0;
  color: var(--dp-color-surface);
  background: transparent;
  font: 700 0.7rem var(--dp-font-body);
  text-decoration: underline;
  cursor: pointer;
}
.dp-nav-user {
  min-width: 16.5rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: auto;
  padding: 1rem 0.65rem 0;
  border-top: 1px solid
    color-mix(in srgb, var(--dp-color-border) 20%, transparent);
}
.dp-user-avatar {
  width: 2.35rem;
  height: 2.35rem;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--dp-color-navy-deep);
  background: color-mix(
    in srgb,
    var(--dp-color-teal) 70%,
    var(--dp-color-surface)
  );
  font-size: 0.72rem;
  font-weight: 800;
}
.dp-user-copy {
  min-width: 0;
}
.dp-user-copy strong,
.dp-user-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dp-user-copy strong {
  color: var(--dp-color-surface);
  font-size: 0.86rem;
}
.dp-user-copy small {
  margin-top: 0.15rem;
  color: color-mix(in srgb, var(--dp-color-surface) 62%, var(--dp-color-teal));
  font-size: 0.68rem;
}
.dp-nav-link:focus-visible,
.dp-nav-close:focus-visible {
  outline: 3px solid var(--dp-color-surface);
  outline-offset: 2px;
}
.dp-nav-backdrop {
  position: fixed;
  z-index: 39;
  inset: 0;
  background: color-mix(in srgb, var(--dp-color-navy-deep) 68%, transparent);
  backdrop-filter: blur(2px);
}
.dp-sidenav.is-mobile {
  position: fixed;
  z-index: 40;
  inset: 0 auto 0 0;
  width: min(19rem, 88vw);
  max-height: 100dvh;
  min-height: 100dvh;
  transform: translateX(-105%);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.dp-sidenav.is-mobile.is-open {
  transform: translateX(0);
}
.dp-sidenav.is-mobile .dp-nav-heading,
.dp-sidenav.is-mobile .dp-nav-group,
.dp-sidenav.is-mobile .dp-nav-link,
.dp-sidenav.is-mobile .dp-nav-user {
  min-width: 0;
}
@media (prefers-reduced-motion: reduce) {
  .dp-sidenav {
    transition: none;
  }
}
</style>
